import type { AxiosResponse } from 'axios';
import type { ClassConstructor } from 'class-transformer';
import { Invoice, Profile } from '../models';
import { Resource } from './resource';

export interface AuthenticateWithEmailPasswordArgs {
  family_name: string;
  given_name: string;
  password: string;
  token: string;
}
export interface AuthenticateWithAuthCodeArgs {
  authorization_code: string;
  id_token: string;
  redirect_uri: string;
}

export interface LoginArgs {
  email: string;
  password: string;
}

export interface LoginResponseRaw {
  access_token: string;
  expires_at: string;
  profile: Record<string, any>;
}

export interface LoginResponse extends LoginResponseRaw {
  profile: Profile;
}

export interface PasswordResetRequestArgs {
  email: string;
}

export interface PasswordResetArgs {
  password: string;
  token: string;
}

export interface PasswordResetValidateResponse {
  email: string;
  organization_name: string;
}

export class Auth extends Resource {
  resourcePath = 'v1/auth';

  async getProviders(email: string): Promise<string | null> {
    return this.api.post(this.path('provider'), { data: { email } }).then((response) => {
      return (response.data?.provider as string | null) ?? null;
    });
  }

  async loginWithPassword(data: LoginArgs): Promise<LoginResponse> {
    return await this.api
      .post(this.path('login'), { data })
      .then((response: AxiosResponse<LoginResponseRaw>) => this.processLoginResponse(response));
  }

  async loginWithAuthCode(data: AuthenticateWithAuthCodeArgs): Promise<LoginResponse> {
    return this.api
      .post(this.path('token'), { data, withCredentials: true })
      .then((response: AxiosResponse<LoginResponseRaw>) => this.processLoginResponse(response));
  }

  async refreshAuthToken(): Promise<LoginResponse> {
    return this.api
      .post(this.path('token/refresh'), { withCredentials: true })
      .then((response: AxiosResponse<LoginResponseRaw>) => this.processLoginResponse(response));
  }

  async registerWithAuthCode(data: AuthenticateWithAuthCodeArgs): Promise<LoginResponse> {
    return this.api
      .post(this.path('register'), { data })
      .then((response: AxiosResponse<LoginResponseRaw>) => this.processLoginResponse(response));
  }

  async registerWithEmailPassword(data: AuthenticateWithEmailPasswordArgs): Promise<LoginResponse> {
    return await this.api
      .post(this.path('invite', 'register'), { data })
      .then((response: AxiosResponse<LoginResponseRaw>) => this.processLoginResponse(response));
  }

  async resetPassword(data: PasswordResetArgs): Promise<LoginResponse> {
    return await this.api
      .post(this.path('password', 'reset'), { data })
      .then((response: AxiosResponse<LoginResponseRaw>) => this.processLoginResponse(response));
  }

  async requestResetPassword(data: PasswordResetRequestArgs): Promise<AxiosResponse> {
    return await this.api.post(this.path('password', 'reset', 'send'), {
      data,
    });
  }

  processLoginResponse(response: AxiosResponse<LoginResponseRaw>): LoginResponse {
    response.data.profile = new Profile(response.data.profile);

    return response.data as LoginResponse;
  }

  async validatePasswordResetToken(token: string): Promise<PasswordResetValidateResponse> {
    return this.api
      .post(this.path('password', 'reset', 'token', 'validate'), {
        data: { token },
      })
      .then((response) => {
        return response.data;
      });
  }
}
